/**
 * The lead form funnel's PostHog event and property names, shared by the browser
 * (lead-form-analytics.ts) and the server (form-actions.ts, the chat's ask route). They follow the
 * homepage's names (homepage-update-1-27-26: src/lib/analytics-utils.ts captureLeadFormEvent and
 * src/lib/homepage-chat/analytics.ts), so one dashboard reads both sites:
 *
 * - The form: lead_form_started (the first step is shown), lead_form_step_viewed,
 *   lead_form_submit_attempted and lead_form_submit_failed (failure_reason crm_rejected | network),
 *   with form_variant and step_name, as on the homepage. Added here in the same style:
 *   lead_form_step_completed, lead_form_field_invalid, and lead_form_crm_accepted (lead_result
 *   accepted, as the homepage chat's homepage_chat_crm_accepted).
 * - The chat's message box: lead_form_chat_message_sent, lead_form_chat_response_received and
 *   lead_form_chat_response_failed (failure_code, failure_stage), as the homepage chat's
 *   homepage_chat_message_sent / _response_received / _response_failed, plus $ai_generation in the
 *   homepage chat's transcript shape.
 */
export const LEAD_FORM_EVENTS = {
  started: 'lead_form_started',
  stepViewed: 'lead_form_step_viewed',
  stepCompleted: 'lead_form_step_completed',
  fieldInvalid: 'lead_form_field_invalid',
  submitAttempted: 'lead_form_submit_attempted',
  submitFailed: 'lead_form_submit_failed',
  crmAccepted: 'lead_form_crm_accepted',
  chatMessageSent: 'lead_form_chat_message_sent',
  chatResponseReceived: 'lead_form_chat_response_received',
  chatResponseFailed: 'lead_form_chat_response_failed',
} as const;

/**
 * Which form the visitor sees (form_variant): the standard lead form, the test prep chat
 * (app/test-prep/chat), or the SEM no-subject test's guided chat (app/guided-chat).
 */
export type FormVariant = 'standard' | 'chat' | 'guided_chat';

/** How an answer was given, in the homepage chat's input_method values. */
export type InputMethod = 'easy_choice' | 'free_text';

/** Where the page runs (chat_environment, the homepage's property and values). */
export type ChatEnvironment = 'local' | 'branch_preview' | 'staging' | 'production' | 'unknown';

/** The homepage chat's intake_step names for the legacy answer keys both forms share. */
const INTAKE_STEPS: Record<string, string> = {
  'who-needs-help': 'learner',
  subject: 'subject',
  'grade-level': 'gradeLevel',
  'grade-level-hs-col': 'gradeLevel',
  how_soon: 'urgency',
  zipcode: 'zipCode',
  'school-district': 'schoolDistrict',
  'full-name': 'name',
  email: 'email',
  phone: 'phone',
};

/** A step's intake_step: the homepage chat's name, or the answer key in the same camelCase style (satSection, whenTaking). */
export const intakeStepFor = (key: string): string => INTAKE_STEPS[key] ?? key.replace(/[-_]+([a-z0-9])/g, (_, letter: string) => letter.toUpperCase());

/** The homepage form's step_name (Grade, Zip, District, Contact) for the steps it has. */
const FORM_STEP_NAMES: Record<string, string> = {
  'grade-level': 'Grade',
  'grade-level-hs-col': 'Grade',
  zipcode: 'Zip',
  'school-district': 'District',
  'full-name': 'Contact',
  email: 'Contact',
  phone: 'Contact',
};

export const stepNameFor = (key: string): string | undefined => FORM_STEP_NAMES[key];

/** chat_environment for a host name, as the homepage chat tells them apart. */
export const environmentForHost = (host: string): ChatEnvironment => {
  const name = host.toLowerCase().split(':')[0];
  if (name === 'localhost' || name === '127.0.0.1') return 'local';
  // Deploy previews and branch deploys are <name>--<site>.netlify.app; the site's own netlify.app address
  // is production (www.varsitytutors.com proxies to it), as the homepage chat tells them apart.
  if (name.endsWith('.netlify.app')) return name.includes('--') ? 'branch_preview' : 'production';
  if (name.includes('staging')) return 'staging';
  if (name === 'www.varsitytutors.com' || name === 'varsitytutors.com') return 'production';
  return 'unknown';
};
